/**
 * 빠른 추가의 시간·날짜·반복 감지 (2026-09-18 시간, 2026-09-25 날짜·반복 — 백로그 AZ3 · BA5).
 *
 * parseQuickTime('오후 3시반 회의') → { start:'15:00', end:'16:30', title:'회의', match:'오후 3시반' }
 * parseQuick('내일 3시 회의', {today, base}) → { title:'회의', date:'2026-09-26', start:'15:00', end:'16:00' }
 * parseQuick('매주 월수금 10시 스탠드업', …) → { …, repeat:{freq:'weekly', interval:1, by_weekday:[1,3,5], end:{kind:'never'}} }
 * 못 찾으면 null. 순수 함수라 빠른 추가(월·주 그리드 · 표 · 프로젝트 허브) · 패널 제목과
 * 단위 테스트(tests/unit/quick-time.test.mjs)가 같은 것을 부른다. 이 파일은 다른 모듈을
 * import 하지 않는다(단위 테스트가 .mjs 사본 하나로 불러온다).
 *
 * 시간 규칙:
 *  - 시각 꼴: 15:00 · 3시 · 3시반 · 3시 20분 · 3pm · 오전/오후/아침/낮/저녁/밤/새벽 접두 · am/pm 접미.
 *    "시" 도 ":" 도 am/pm 도 없는 맨 숫자는 시각이 아니고("회의 3건"), "3시간" 도 아니다.
 *  - 범위: A~B · A-B · A–B · A부터 B까지 · A에서 B까지 (양쪽 모두 시각).
 *    끝에 오전·오후가 없으면 시작의 것을 물려받고("오전 3시부터 5시까지" → 03:00–05:00),
 *    그래도 끝이 시작보다 앞이면 끝을 오후로 올린다("10시~2시" → 10:00–14:00).
 *  - 접두·접미가 없는 "H시": 1~6시는 오후, 7~12시는 그대로(12 = 정오). "HH:MM" 은 24시간 그대로.
 *  - 시각 하나면 1시간짜리, 자정을 넘기면 23:59 에서 자른다. 23:59 시작은 감지하지 않는다.
 *  - 시간 문구를 뗀 제목이 비면 감지하지 않는다(제목은 남아야 한다).
 * 날짜 규칙 (parseQuick 의 둘째 조각):
 *  - 상대 표현은 **오늘**에서 센다: 오늘 · 내일 · 모레 · 글피, 이번주/다음주(담주·차주)/다다음주 + 요일
 *    (말하는 주는 **월요일 시작** — 「이번주 일요일」 은 다가오는 일요일이다; 격자의 주 시작 요일
 *    opts.weekStart 와 무관하다, XA47), "X요일" 하나는 오늘부터 처음 오는 그 요일(오늘 포함).
 *  - 날짜 꼴: 2026-10-05 · 2026.10.5 · 2026/10/5 · 10/2 · 10월 2일 · 28일. 연도가 없으면 기준 날짜
 *    (base — 그리드 칸·표의 날짜, 없으면 오늘)의 해, "28일" 은 그 달이다. 없는 날짜(9/31)는 날짜가 아니다.
 *    "3일간"·"3일마다"·"3일째" 는 날짜가 아니다.
 * 반복 규칙 (첫째 조각, 서버의 SeriesRule JSON — 반복 스펙 §3.2 · §10):
 *  - 매일 · N일마다 · 매주 [요일들] · 격주/N주마다 [요일들] · 평일마다/매주 평일 · 매월/매달 [N일 | 첫째…넷째·마지막 X요일]
 *    · 매년/매해. 요일들은 월수금 · 월·수 · 월,수 · 월요일 꼴이고 뒤에 한글이 붙으면 요일이 아니다("매주 목표").
 *  - 시작일은 날짜(없으면 기준 날짜)에서 규칙이 처음 맞는 날로 맞춘다(매주 월 → 다음 월요일, 매월 15일 → 다음 15일,
 *    매년 3월 1일 → 이미 지났으면 내년). 종료는 언제나 "없음" — 끝은 패널에서 정한다.
 */

const MERIDIAN = '(오전|오후|아침|낮|저녁|밤|새벽|am|pm|AM|PM)';
// groups: prefix · hour · minute(:MM) · minute(시 N분 | 반) · suffix(am/pm)
const TIME = '(?:' + MERIDIAN + '\\s*)?(\\d{1,2})(?::(\\d{2})|\\s*시(?!간)(?:\\s*(반|\\d{1,2}\\s*분))?|(?=\\s*(?:am|pm|AM|PM)))\\s*(am|pm|AM|PM)?';
const TIME_SINGLE = new RegExp('(?:^|[^\\d:])(' + TIME + ')(?![\\d:])');
const RANGE_SEP = '\\s*(?:~|-|–|—|부터|에서)\\s*';
const TIME_RANGE = new RegExp('(?:^|[^\\d:])(' + TIME + RANGE_SEP + TIME + '(?:\\s*까지)?)(?![\\d:])');

function pad(n) { return (n < 10 ? '0' : '') + n; }

// toMinutes turns one matched token into minutes since midnight, or -1 when
// it is not a real time.
function toMinutes(prefix, hourStr, colonMin, siMin, suffix) {
    const hasColon = colonMin != null;
    let h = parseInt(hourStr, 10);
    let m = 0;
    if (hasColon) m = parseInt(colonMin, 10);
    else if (siMin != null) m = siMin === '반' ? 30 : parseInt(siMin, 10);
    if (Number.isNaN(h) || Number.isNaN(m) || m > 59) return -1;
    const mer = (prefix || suffix || '').toLowerCase();
    if (mer) {
        if (h > 12) return -1; // "오후 15시" 는 말이 안 된다
        switch (mer) {
        case '오전': case '아침': case 'am': case '새벽':
            if (h === 12) h = 0;
            break;
        case '오후': case '저녁': case 'pm':
            if (h < 12) h += 12;
            break;
        case '밤':
            if (h === 12) h = 0;
            else if (h < 12) h += 12;
            break;
        case '낮':
            if (h <= 6) h += 12; // 낮 3시 = 15:00 · 낮 11시 = 11:00 · 낮 12시 = 12:00
            break;
        default:
            break;
        }
    } else if (!hasColon && h >= 1 && h <= 6) {
        h += 12; // "H시" without a meridian: office hours
    }
    if (h > 23) return -1;
    return h * 60 + m;
}

function fmt(min) { return pad(Math.floor(min / 60)) + ':' + pad(min % 60); }

function cleanTitle(text, matchStart, matchLen) {
    const rest = text.slice(0, matchStart) + ' ' + text.slice(matchStart + matchLen);
    return rest.replace(/\s+/g, ' ').replace(/^[\s·,\-–—:]+|[\s·,\-–—:]+$/g, '').trim();
}

/**
 * parseQuickTime(text) → { start, end, title, match } | null
 */
export function parseQuickTime(text) {
    const src = String(text || '');
    if (!src.trim()) return null;
    let startMin = -1;
    let endMin = -1;
    let matched = '';
    let matchStart = 0;
    let m = TIME_RANGE.exec(src);
    if (m) {
        // m[1] = whole span; groups 2..6 = first time, 7..11 = second time
        startMin = toMinutes(m[2], m[3], m[4], m[5], m[6]);
        const endOwnMeridian = m[7] || m[11];
        const inherited = endOwnMeridian ? null : (m[2] || m[6] || null);
        endMin = toMinutes(m[7] || inherited, m[8], m[9], m[10], m[11]);
        if (startMin >= 0 && endMin >= 0) {
            if (endMin <= startMin && !endOwnMeridian && endMin < 12 * 60) endMin += 12 * 60; // "10시~2시"
            if (endMin <= startMin) endMin = -1; // 끝을 못 믿겠으면 한 시간짜리로
            matched = m[1];
            matchStart = m.index + m[0].length - m[1].length;
        } else {
            startMin = -1;
            endMin = -1;
            m = null;
        }
    }
    if (!m) {
        m = TIME_SINGLE.exec(src);
        if (!m) return null;
        startMin = toMinutes(m[2], m[3], m[4], m[5], m[6]);
        if (startMin < 0) return null;
        matched = m[1];
        matchStart = m.index + m[0].length - m[1].length;
    }
    if (startMin >= 23 * 60 + 59) return null;
    if (endMin < 0) endMin = Math.min(startMin + 60, 23 * 60 + 59);
    const title = cleanTitle(src, matchStart, matched.length);
    if (!title) return null;
    return { start: fmt(startMin), end: fmt(endMin), title, match: matched.trim() };
}

// ── 날짜 ──────────────────────────────────────────────────────────────

const WD_CHARS = '일월화수목금토';
const pad2 = (n) => (n < 10 ? '0' : '') + n;
function parseISO(ds) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ds || ''));
    if (!m) return null;
    return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
}
function iso(d) { return d.getUTCFullYear() + '-' + pad2(d.getUTCMonth() + 1) + '-' + pad2(d.getUTCDate()); }
function addDaysISO(ds, n) { const d = parseISO(ds); if (!d) return ds; d.setUTCDate(d.getUTCDate() + n); return iso(d); }
function weekdayOf(ds) { const d = parseISO(ds); return d ? d.getUTCDay() : 0; }
// validYMD returns the ISO date when y-m-d is a real day, else ''.
function validYMD(y, m, d) {
    if (m < 1 || m > 12 || d < 1 || d > 31) return '';
    const t = new Date(Date.UTC(y, m - 1, d));
    return t.getUTCMonth() === m - 1 && t.getUTCDate() === d ? iso(t) : '';
}
function localToday() {
    const n = new Date();
    return n.getFullYear() + '-' + pad2(n.getMonth() + 1) + '-' + pad2(n.getDate());
}
// weekStartIndex reads opts.weekStart: 'mon' / 1 → Monday (1), anything else Sunday (0) — XA21.
function weekStartIndex(v) { return v === 'mon' || v === 1 || v === '1' ? 1 : 0; }
// weekStartOf: the first day of the week (starting on weekday `first`) that contains ds.
function weekStartOf(ds, first) { return addDaysISO(ds, -((weekdayOf(ds) - first + 7) % 7)); }

const HANGUL = '[가-힣]';
// Each date pattern is tried in turn on the text; the first that yields a
// valid date wins. `fn(m)` returns the ISO date or ''.
function datePatterns(today, base, first) {
    const bd = parseISO(base) || parseISO(today);
    const baseY = bd ? bd.getUTCFullYear() : new Date().getFullYear();
    const baseM = bd ? bd.getUTCMonth() + 1 : 1;
    const wdIdx = (ch) => WD_CHARS.indexOf(ch);
    return [
        { re: /(?:^|[^\d])((\d{4})[-./](\d{1,2})[-./](\d{1,2}))(?![\d./-])/, fn: (m) => validYMD(+m[2], +m[3], +m[4]) },
        { re: new RegExp('(?:^|[^\\d])((\\d{1,2})\\s*월\\s*(\\d{1,2})\\s*일)(?!간|동안|째|치|마다)'), fn: (m) => validYMD(baseY, +m[2], +m[3]) },
        { re: /(?:^|[^\d./])((\d{1,2})\/(\d{1,2}))(?![\d/]|\s*분기)/, fn: (m) => validYMD(baseY, +m[2], +m[3]) }, // "3/4분기" 는 날짜가 아니다
        { re: new RegExp('(?:^|' + '[^\\d가-힣])((다다음\\s*주|다음\\s*주|담\\s*주|차\\s*주|이번\\s*주|금주)\\s*([' + WD_CHARS + '])(?:요일)?)(?!' + HANGUL + ')'), fn: (m) => {
            const w = m[2].replace(/\s+/g, '');
            const off = w === '다다음주' ? 14 : (w === '이번주' || w === '금주') ? 0 : 7;
            // 말하는 주는 월요일 시작이다 — 격자의 주 시작 요일(first)과 무관하게 「이번주 일요일」 은 이번
            // 주말, 곧 다가오는 일요일이다 (XA47 결정)
            return addDaysISO(weekStartOf(today, 1), off + ((wdIdx(m[3]) - 1 + 7) % 7));
        } },
        { re: new RegExp('(?:^|[^가-힣])(([' + WD_CHARS + '])요일)(?!' + HANGUL + ')'), fn: (m) => addDaysISO(today, (wdIdx(m[2]) - weekdayOf(today) + 7) % 7) },
        { re: new RegExp('(?:^|[^가-힣])(오늘|내일|모레|글피)(?!' + HANGUL + ')'), fn: (m) => addDaysISO(today, { 오늘: 0, 내일: 1, 모레: 2, 글피: 3 }[m[1]]) },
        { re: new RegExp('(?:^|[^\\d./가-힣])((\\d{1,2})\\s*일)(?!간|동안|째|치|마다|' + HANGUL + ')'), fn: (m) => validYMD(baseY, baseM, +m[2]) },
    ];
}

// removeSpan drops text[start, start+len) and tidies the seams.
function removeSpan(text, start, len) {
    return (text.slice(0, start) + ' ' + text.slice(start + len)).replace(/\s+/g, ' ').trim();
}

function findDate(text, today, base, first) {
    for (const p of datePatterns(today, base, first || 0)) {
        const m = p.re.exec(text);
        if (!m) continue;
        const date = p.fn(m);
        if (!date) continue;
        const at = m.index + m[0].length - m[1].length;
        return { date, rest: removeSpan(text, at, m[1].length) };
    }
    return null;
}

// ── 반복 ──────────────────────────────────────────────────────────────

const NTH = { 첫째: 1, 첫번째: 1, 둘째: 2, 두번째: 2, 셋째: 3, 세번째: 3, 넷째: 4, 네번째: 4, 마지막: -1 };
// WD_LIST: 월수금 · 월·수 · 월, 수 · 월요일 — a weekday char must not run into another syllable.
const WD_LIST = '((?:[' + WD_CHARS + '](?:요일)?(?:\\s*[·,/]\\s*)?)+)(?:마다|에|엔)?(?!' + HANGUL + ')';
function weekdayList(s) {
    const out = [];
    for (const ch of String(s || '').replace(/요일/g, '')) { // "요일" 의 일 은 일요일이 아니다
        const i = WD_CHARS.indexOf(ch);
        if (i >= 0 && out.indexOf(i) < 0) out.push(i);
    }
    return out.sort((a, b) => a - b);
}
const REPEATS = [
    { re: new RegExp('(?:^|[^가-힣])((?:매주\\s*평일|매\\s*평일|평일\\s*마다|평일마다)(?:에)?)(?!' + HANGUL + ')'), rule: () => ({ freq: 'weekly', interval: 1, by_weekday: [1, 2, 3, 4, 5] }) },
    { re: new RegExp('(?:^|[^가-힣])(매일|날마다)(?!' + HANGUL + ')'), rule: () => ({ freq: 'daily', interval: 1 }) },
    { re: new RegExp('(?:^|[^\\d가-힣])((\\d{1,2})\\s*일\\s*마다)(?!' + HANGUL + ')'), rule: (m) => ({ freq: 'daily', interval: +m[2] }) },
    { re: new RegExp('(?:^|[^\\d가-힣])((?:매주|격주|(\\d{1,2})\\s*주\\s*마다)(?:\\s*' + WD_LIST + ')?)'), rule: (m) => {
        const iv = /^격주/.test(m[1]) ? 2 : (m[2] ? +m[2] : 1);
        return { freq: 'weekly', interval: iv, by_weekday: weekdayList(m[3]) };
    } },
    // 날·요일 뒤의 조사(에·엔·마다)도 이 반복의 일부다 — 빼면 「매달 1일에」 가 날을 잃고(오늘 날짜로 매달) 제목에 남았다
    { re: new RegExp('(?:^|[^가-힣])((?:매월|매달)(?:\\s*(?:(\\d{1,2})\\s*일|(첫째|첫번째|둘째|두번째|셋째|세번째|넷째|네번째|마지막)\\s*([' + WD_CHARS + '])(?:요일)?)(?:에|엔|마다)?)?)(?!' + HANGUL + ')'), rule: (m) => {
        if (m[3]) return { freq: 'monthly', interval: 1, monthly: { kind: 'nth', nth: NTH[m[3]], weekday: WD_CHARS.indexOf(m[4]) }, day: 0 };
        return { freq: 'monthly', interval: 1, monthly: { kind: 'day' }, day: m[2] ? +m[2] : 0 };
    } },
    { re: new RegExp('(?:^|[^가-힣])(매년|매해)(?!' + HANGUL + ')'), rule: () => ({ freq: 'yearly', interval: 1 }) },
];
function findRepeat(text) {
    for (const p of REPEATS) {
        const m = p.re.exec(text);
        if (!m) continue;
        const rule = p.rule(m);
        if (!rule || rule.interval < 1 || rule.interval > 99) continue;
        if (rule.day && (rule.day < 1 || rule.day > 31)) continue;
        const at = m.index + m[0].length - m[1].length;
        return { rule, rest: removeSpan(text, at, m[1].length) };
    }
    return null;
}

// nthWeekdayOf: the nth (1..4 or -1 = last) weekday of the month of (y, m), '' when none.
function nthWeekdayOf(y, m, nth, wd) {
    if (nth === -1) {
        const last = new Date(Date.UTC(y, m, 0));
        last.setUTCDate(last.getUTCDate() - ((last.getUTCDay() - wd + 7) % 7));
        return iso(last);
    }
    const first = new Date(Date.UTC(y, m - 1, 1));
    const d = new Date(Date.UTC(y, m - 1, 1 + ((wd - first.getUTCDay() + 7) % 7) + 7 * (nth - 1)));
    return d.getUTCMonth() === m - 1 ? iso(d) : '';
}
// alignStart moves `from` to the first day the rule actually produces.
function alignStart(rule, from) {
    if (rule.freq === 'weekly' && rule.by_weekday && rule.by_weekday.length) {
        for (let i = 0; i < 7; i++) {
            const d = addDaysISO(from, i);
            if (rule.by_weekday.indexOf(weekdayOf(d)) >= 0) return d;
        }
    }
    if (rule.freq === 'monthly') {
        const f = parseISO(from);
        let y = f.getUTCFullYear();
        let m = f.getUTCMonth() + 1;
        for (let i = 0; i < 24; i++) {
            const d = rule.monthly && rule.monthly.kind === 'nth'
                ? nthWeekdayOf(y, m, rule.monthly.nth, rule.monthly.weekday)
                : validYMD(y, m, rule.day || f.getUTCDate());
            if (d && d >= from) return d;
            m += 1;
            if (m > 12) { m = 1; y += 1; }
        }
    }
    return from;
}

/**
 * parseQuick(text, { today, base, needDate, allowRepeat, weekStart }) → { title, date?, start?, end?, repeat? } | null
 *
 * 반복 → 날짜 → 시각 순으로 떼어 낸다. 아무것도 못 찾거나 제목이 비면 null. needDate 면
 * 날짜나 반복이 없을 때 null(프로젝트 허브의 표: 시각만으로는 미정 작업에 붙일 곳이 없다).
 * allowRepeat:false 면 반복 말은 제목에 남는다(패널 제목 — 이미 있는 일정).
 */
export function parseQuick(text, opts) {
    const o = opts || {};
    const today = o.today || localToday();
    const base = o.base || today;
    let rest = String(text || '').replace(/\s+/g, ' ').trim();
    if (!rest) return null;
    const out = {};
    let rep = null;
    if (o.allowRepeat !== false) {
        rep = findRepeat(rest);
        if (rep) rest = rep.rest;
    }
    const dt = findDate(rest, today, base, weekStartIndex(o.weekStart));
    if (dt) { rest = dt.rest; out.date = dt.date; }
    const tm = parseQuickTime(rest);
    if (tm) { rest = tm.title; out.start = tm.start; out.end = tm.end; }
    if (!rep && !dt && !tm) return null;
    if (o.needDate && !rep && !dt) return null;
    const title = rest.replace(/^[\s·,\-–—:]+|[\s·,\-–—:]+$/g, '').trim();
    if (!title) return null;
    out.title = title;
    if (rep) {
        const rule = rep.rule;
        let from = out.date || base;
        if (rule.freq === 'yearly' && out.date && out.date < base) {
            // "매년 3월 1일" — 올해 날짜가 이미 지났으면 내년부터
            const d = parseISO(out.date);
            from = validYMD(d.getUTCFullYear() + 1, d.getUTCMonth() + 1, d.getUTCDate()) || out.date;
        }
        if (rule.freq === 'weekly' && (!rule.by_weekday || !rule.by_weekday.length)) rule.by_weekday = [weekdayOf(from)];
        out.date = alignStart(rule, from);
        const clean = { freq: rule.freq, interval: rule.interval };
        if (rule.freq === 'weekly') clean.by_weekday = rule.by_weekday.slice();
        if (rule.freq === 'monthly') clean.monthly = rule.monthly.kind === 'nth' ? { kind: 'nth', nth: rule.monthly.nth, weekday: rule.monthly.weekday } : { kind: 'day' };
        clean.end = { kind: 'never' };
        out.repeat = clean;
    }
    return out;
}

const NTH_LABEL = { 1: '첫째', 2: '둘째', 3: '셋째', 4: '넷째', '-1': '마지막' };
// repeatLabel: a short summary for the chip ("매주 월·수", "평일마다", "매월 첫째 월요일", "2주마다 목").
function repeatLabel(r, date) {
    const iv = r.interval || 1;
    const days = (r.by_weekday || []).map((i) => WD_CHARS[i]).join('·');
    switch (r.freq) {
    case 'daily': return iv === 1 ? '매일' : iv + '일마다';
    case 'weekly':
        if (iv === 1 && (r.by_weekday || []).join(',') === '1,2,3,4,5') return '평일마다';
        return (iv === 1 ? '매주 ' : iv === 2 ? '격주 ' : iv + '주마다 ') + days;
    case 'monthly':
        if (r.monthly && r.monthly.kind === 'nth') return '매월 ' + NTH_LABEL[String(r.monthly.nth)] + ' ' + WD_CHARS[r.monthly.weekday] + '요일';
        return '매월 ' + (parseISO(date) ? parseISO(date).getUTCDate() : '') + '일';
    case 'yearly': { const d = parseISO(date); return '매년' + (d ? ' ' + (d.getUTCMonth() + 1) + '/' + d.getUTCDate() : ''); }
    default: return '';
    }
}
function dayLabel(ds, weekdays) {
    const d = parseISO(ds);
    if (!d) return ds;
    const names = String(weekdays || '일,월,화,수,목,금,토').split(',');
    return (d.getUTCMonth() + 1) + '/' + d.getUTCDate() + ' (' + (names[d.getUTCDay()] || '') + ')';
}

/**
 * quickChipLabel(q, { weekdays, fromFmt }) — the chip text: "9/26 (토) · 15:00–16:00 · 회의",
 * "↻ 매주 월·수·금 · 9/25 (금)부터 · 10:00–11:00 · 스탠드업". weekdays = the page's CSV
 * (일,월,…) so the day name follows the language; repeat words stay Korean like the input.
 */
export function quickChipLabel(q, opts) {
    if (!q) return '';
    const o = opts || {};
    const parts = [];
    if (q.repeat) {
        parts.push('↻ ' + repeatLabel(q.repeat, q.date));
        if (q.date) parts.push(String(o.fromFmt || '%s부터').replace('%s', dayLabel(q.date, o.weekdays)));
    } else if (q.date) {
        parts.push(dayLabel(q.date, o.weekdays));
    }
    if (q.start) parts.push(formatQuickRange(q.start, q.end));
    parts.push(q.title);
    return parts.join(' · ');
}

/** formatQuickRange('15:00','16:00') → '15:00–16:00' */
export function formatQuickRange(start, end) {
    return end && end !== start ? start + '–' + end : start;
}

/**
 * bindQuickTime(input, chip, opts) — 빠른 추가 입력창에 감지를 건다.
 * chip 은 빈 요소; 감지되면 quickChipLabel 문구와 ✕ 가 보인다(hidden 속성으로 숨김).
 * ✕ 는 입력을 비울 때까지 감지를 끈다(mousedown 을 막아 입력창의 blur 저장이 먼저
 * 달리지 않게 한다). opts:
 *   offLabel     ✕ 의 툴팁
 *   base         기준 날짜 'YYYY-MM-DD' 또는 그것을 돌려주는 함수(그리드 칸·표의 날짜) — 없으면 오늘
 *   today        오늘 'YYYY-MM-DD' (없으면 브라우저의 오늘)
 *   needDate     날짜·반복이 없으면 감지하지 않는다(프로젝트 허브의 표: 미정 작업)
 *   allowRepeat  false 면 "매주 …" 를 반복으로 읽지 않는다
 *   weekdays     요일 이름 CSV (일,월,…) — 칩의 날짜 표기
 *   fromFmt      반복 칩의 시작일 꼴 ("%s부터")
 *   weekStart    주 시작 요일 'sun' | 'mon' ("이번주 일" 같은 말, XA21) — 루트의 data-week-start
 * 돌려주는 객체:
 *   time()        현재 감지값 | null (parseQuick 의 모양)
 *   applyTo(body) URLSearchParams 에 제목·날짜·시각·반복을 얹는다: 날짜가 있으면 start_date/end_date 를
 *                 그 날로(원래 기간의 길이는 지킨다), 시각이 있으면 all_day 를 지우고 start/end_time,
 *                 반복이 있으면 repeat_json.
 */
export function bindQuickTime(input, chip, opts) {
    const o = opts || {};
    const label = document.createElement('span');
    label.className = 'cal-quick-time-label';
    const off = document.createElement('button');
    off.type = 'button';
    off.className = 'cal-quick-time-off';
    off.textContent = '✕';
    off.title = o.offLabel || '시간 감지 끄기';
    off.setAttribute('aria-label', off.title);
    chip.classList.add('cal-quick-time');
    chip.hidden = true;
    chip.append(label, off);
    let cur = null;
    let muted = false;
    const baseOf = () => (typeof o.base === 'function' ? o.base() : o.base) || '';
    function update() {
        if (!input.value.trim()) muted = false;
        cur = muted ? null : parseQuick(input.value, { today: o.today, base: baseOf(), needDate: !!o.needDate, allowRepeat: o.allowRepeat !== false, weekStart: o.weekStart });
        chip.hidden = !cur;
        chip.dataset.kind = cur ? (cur.repeat ? 'repeat' : cur.date ? 'date' : 'time') : '';
        if (cur) label.textContent = quickChipLabel(cur, { weekdays: o.weekdays, fromFmt: o.fromFmt });
    }
    input.addEventListener('input', update);
    off.addEventListener('mousedown', (e) => e.preventDefault());
    off.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        muted = true;
        update();
        input.focus();
    });
    update();
    return {
        time: () => cur,
        refresh: update,
        applyTo(body) {
            if (!cur) return body;
            body.set('title', cur.title);
            if (cur.date) {
                const s0 = body.get('start_date') || '';
                const e0 = body.get('end_date') || '';
                const span = s0 && e0 && e0 >= s0 ? Math.round((parseISO(e0) - parseISO(s0)) / 86400000) : 0;
                body.set('start_date', cur.date);
                body.set('end_date', addDaysISO(cur.date, span));
            }
            if (cur.start) {
                body.delete('all_day');
                body.set('start_time', cur.start);
                body.set('end_time', cur.end);
            }
            if (cur.repeat) body.set('repeat_json', JSON.stringify(cur.repeat));
            return body;
        },
    };
}
